/**
 * CommunityCard — reusable card for discovery grid + rails.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Lock, Globe, Mail } from 'lucide-react';
import { TiltCard, cx } from '@/components/site/primitives';
import { VISIBILITY_META } from './constants';
import { formatCount } from './utils/communityHelpers';
import JoinButton from './JoinButton';

const spineTone = {
  coral: 'from-coral to-[#b85e58]',
  yellow: 'from-yellow to-[#e0b164]',
  sage: 'from-sage to-[#a9c39d]',
  blue: 'from-blue to-[#6b808b]',
};

const dotVar = { coral: 'var(--color-coral)', yellow: 'var(--color-yellow)', sage: 'var(--color-sage)', blue: 'var(--color-blue)' };
const TONE_CYCLE = ['coral', 'yellow', 'sage', 'blue'];

const VISIBILITY_ICON = { public: Globe, private: Lock, restricted: Mail };

function threeTones(tone) {
  const i = TONE_CYCLE.indexOf(tone);
  return [0, 1, 2].map(k => TONE_CYCLE[(i + k) % 4]);
}

export default function CommunityCard({ community, reason }) {
  const Icon = VISIBILITY_ICON[community.visibility] ?? Globe;

  return (
    <TiltCard max={5} className="group flex h-full flex-col rounded-2xl border border-line bg-card p-5 shadow-soft transition-shadow duration-300 hover:shadow-card">
      <Link to={`/app/communities/${community.id}`} className="flex flex-1 flex-col focus-visible:outline-none">
        <div className="flex items-start justify-between">
          <div className="flex -space-x-2" aria-hidden="true">
            {threeTones(community.tone).map((t, i) => (
              <span key={i} className={cx('h-9 w-7 rounded-[3px] bg-gradient-to-br shadow-sm ring-2 ring-card', spineTone[t])} />
            ))}
          </div>
          <Icon className="h-4 w-4 shrink-0 text-subtle" aria-label={VISIBILITY_META[community.visibility]?.label} />
        </div>

        <h3 className="mt-4 font-display text-xl font-semibold leading-snug text-ink">{community.name}</h3>
        {reason && <p className="mt-1 text-xs font-semibold text-coral">{reason}</p>}
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-subtle">{community.tagline}</p>

        <div className="mt-4 flex items-center justify-between text-xs text-subtle">
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" /> {formatCount(community.memberCount)} members
          </span>
          {community.activeNow > 0 && (
            <span className="inline-flex items-center gap-1.5 font-medium text-ink">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: dotVar[community.tone] }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: dotVar[community.tone] }} />
              </span>
              {formatCount(community.activeNow)} active now
            </span>
          )}
        </div>
      </Link>

      <div className="mt-4">
        <JoinButton community={community} size="sm" className="w-full justify-center" />
      </div>
    </TiltCard>
  );
}
